import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { RecurrenceAvailabilityPreview } from "@/features/RecurringRequests/types";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { RestrictionInfoBarStack } from "@whoosh/features/src/Restrictions/components/RestrictionInfoBarStack/RestrictionInfoBarStack";
import { useGetBookingRestrictionStatuses } from "@whoosh/features/src/Restrictions/hooks/useGetBookingRestrictionStatuses/useGetBookingRestrictionStatuses";

import { useUpdateMemberNotesForBooking } from "../../hooks/useUpdateMemberNotesForBooking";
import {
  useIsRequestConfigRequestWindowClosed,
  useShouldPresentBookingAsLockedRequest,
} from "../BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { RequestDetails } from "../BookingRequest/components/RequestDetails/RequestDetails";
import { RecurringRequestSeriesCard } from "../RecurringRequestSeriesCard/RecurringRequestSeriesCard";
import { RecurringRequestPreviewStatus } from "../RecurringRequestSeriesCard/components/RecurringRequestPreviewStatus";
import { useRecurringRequestSeriesPreview } from "../RecurringRequestSeriesCard/hooks/useRecurringRequestSeriesPreview";
import { RequestRestrictionBannerStack } from "../RequestRestrictionBannerStack/RequestRestrictionBannerStack";
import { BookingDetailsView_Booking$key } from "./__generated__/BookingDetailsView_Booking.graphql";
import { ApplyPunchPassModal } from "./components/ApplyPunchPassModal/ApplyPunchPassModal";
import { BookingActionsSection } from "./components/BookingActionsSection";
import { BookingHeader } from "./components/BookingHeader";
import { RoundList } from "./components/RoundList";
import { useIsAnyDynamicRateOnBookingLocked } from "./hooks/useIsAnyDynamicRateOnBookingLocked";
import { useIsViewMoreVisible } from "./hooks/useIsViewMoreVisible/useIsViewMoreVisible";

type Props = {
  bookingRef: BookingDetailsView_Booking$key | null;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
  onRecurrencePreviewChange?: (
    preview: RecurrenceAvailabilityPreview | null
  ) => void;
  requestGroupCount?: number;
  requestRoundBookingIndexes?: readonly (number | null | undefined)[];
};

export const BookingDetailsView = ({
  bookingRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
  onRecurrencePreviewChange,
  requestGroupCount = 1,
  requestRoundBookingIndexes,
}: Props) => {
  const data = useFragment(BookingDetailsViewFragmentString, bookingRef);
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();
  const pendingRecurrence = route.params?.params?.pendingRecurrence ?? null;
  const previewState = useRecurringRequestSeriesPreview({
    bookingRef: data ?? null,
    pendingRecurrence,
  });

  const recurrenceAvailabilityPreview = previewState.preview;

  useEffect(() => {
    onRecurrencePreviewChange?.(recurrenceAvailabilityPreview);
  }, [onRecurrencePreviewChange, recurrenceAvailabilityPreview]);

  const [viewMore, setViewMore] = useState(false);
  const isViewMoreVisible = useIsViewMoreVisible(data ?? null);

  const [updateMemberNotesForBooking, isUpdating] =
    useUpdateMemberNotesForBooking();

  const onSaveBookingNote = (notes: string) => {
    updateMemberNotesForBooking({
      bookingId: data?.id ?? "",
      notes,
    });
  };

  const isDurationBasedFacility =
    data?.startSlot?.agenda?.facility.type &&
    data?.startSlot?.agenda?.facility.type !== "GOLF_COURSE";

  const { roundRestrictions, groupRestrictions } =
    useGetBookingRestrictionStatuses({
      fragmentRef: data ?? null,
    });

  const isAnyDynamicRateOnBookingLocked = useIsAnyDynamicRateOnBookingLocked(
    data ?? null
  );

  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    data ?? null
  );
  const isClosedRequestWindow = useIsRequestConfigRequestWindowClosed(
    data ?? null
  );
  const isRequestPresentation =
    data?.startSlot?.__typename === "QueueSlot" || shouldPresentAsRequest;

  return (
    <Box
      padding={5}
      paddingTop={6}
      gap={5}
      backgroundColor="surface-primary"
      testID="booking-details-view"
    >
      <BookingHeader
        isViewMoreVisible={isViewMoreVisible}
        isQueueSlot={isRequestPresentation}
        viewMore={viewMore}
        setViewMore={setViewMore}
      />

      <RequestRestrictionBannerStack
        bookingRef={data ?? null}
        requestGroupCount={requestGroupCount}
      />

      <RestrictionInfoBarStack
        restrictions={
          groupRestrictions?.filter(
            (r): r is NonNullable<typeof r> => r !== undefined
          ) ?? []
        }
        restrictionContext="Patron"
      />
      <RequestDetails bookingRef={data ?? null} />
      <RecurringRequestPreviewStatus
        state={previewState}
        onRetry={previewState.retry}
      />
      <RecurringRequestSeriesCard
        bookingRef={data ?? null}
        currentSessionUserClientId={currentSessionUserClientId}
        placement="details"
        pendingRecurrence={pendingRecurrence}
        availabilityPreview={recurrenceAvailabilityPreview}
      />

      {isRequestPresentation ? (
        <Box height={1} backgroundColor="surface-dividers" width="100%" />
      ) : null}

      <RoundList
        data={data ?? null}
        viewMore={viewMore}
        roundRestrictions={
          roundRestrictions
            ? roundRestrictions.map((round) =>
                round.filter((r): r is NonNullable<typeof r> => r !== undefined)
              )
            : []
        }
        isDurationBasedFacility={isDurationBasedFacility ?? false}
        currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
        currentSessionUserClientId={currentSessionUserClientId}
        setViewMore={setViewMore}
        isLockedRequest={shouldPresentAsRequest}
        requestGroupCount={requestGroupCount}
        requestRoundBookingIndexes={requestRoundBookingIndexes}
      />

      {isUpdating ? <LoadingSpinner /> : null}

      <BookingActionsSection
        data={data ?? null}
        onSaveBookingNote={onSaveBookingNote}
        isAnyDynamicRateOnBookingLocked={
          isAnyDynamicRateOnBookingLocked ?? false
        }
        isLockedRequest={isClosedRequestWindow}
        isRequestPresentation={isRequestPresentation}
        currentSessionUserClientId={currentSessionUserClientId}
        currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
        recurringRequestActions={
          <RecurringRequestSeriesCard
            bookingRef={data ?? null}
            currentSessionUserClientId={currentSessionUserClientId}
            placement="actions"
            pendingRecurrence={pendingRecurrence}
            availabilityPreview={recurrenceAvailabilityPreview}
          />
        }
      />
      <ApplyPunchPassModal
        bookingRef={data ?? null}
        bookingType={data?.type ?? "STANDARD"}
        membershipTypeId={currentSessionMemberShipTypeId}
      />
    </Box>
  );
};

const BookingDetailsViewFragmentString = graphql`
  fragment BookingDetailsView_Booking on Booking {
    id
    type
    startSlot {
      __typename
      agenda {
        facility {
          type
        }
      }
    }
    notesFromMember
    ...useGetBookingRestrictionStatusesFragment
    ...CancelBookingButton_Booking
    ...MoveBookingButton_Booking
    ...EditBookingButton_Booking
    ...BookingActionsSection_Booking
    ...useIsViewMoreVisible_Booking
    ...RequestDetails_booking
    ...RecurringRequestSeriesCard_booking
    ...useRecurringRequestSeriesPreview_booking
    ...useRequestRestrictionStatuses_booking
    ...useIsAnyDynamicRateOnBookingLocked_Booking
    ...useShouldPresentBookingAsLockedRequest_booking
    ...RoundList_Booking
    ...useGetSummaryCalculations_Booking
    ...ApplyPunchPassModalContent_Booking
    ownerClient {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
  }
`;
